import { Command } from 'cmdk';
import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useObjectScope } from '@/shared/object-scope';
import {
  can,
  canAccess,
  canManage,
  hasAnyRole,
  ROLE_GROUPS,
  useAuth,
  type Role,
} from '@/shared/auth';
import type { PrivilegeId } from '@stayon/auth-core';
import { CLEANING_QUEUE_SURFACE, DASHBOARD_SURFACE } from '@/shared/auth/privilegeSurfaces';
import { DialogDescription, DialogTitle } from '@/shared/components/Dialog';
import { I } from '@/shared/icons';
import { useQuickSearch } from './useQuickSearch';
import { groupHits, hitToNavigation, type HitNavigation } from './searchNavigation';

const NAV_ITEMS: { label: string; to: string; roles?: Role[]; privilege?: PrivilegeId }[] = [
  {
    label: DASHBOARD_SURFACE.label,
    to: DASHBOARD_SURFACE.route,
    privilege: DASHBOARD_SURFACE.privilege,
  },
  { label: 'Objekte', to: '/property-groups', roles: ROLE_GROUPS.operationsOwner },
  { label: 'Apartments', to: '/properties', roles: ROLE_GROUPS.operationsOwner },
  { label: 'Belegung', to: '/calendar', roles: ROLE_GROUPS.operationsOwner },
  { label: 'Buchungen', to: '/bookings', roles: ROLE_GROUPS.operationsOwner },
  { label: 'Parkplätze', to: '/parking', roles: ROLE_GROUPS.staff },
  {
    label: CLEANING_QUEUE_SURFACE.label,
    to: CLEANING_QUEUE_SURFACE.route,
    privilege: CLEANING_QUEUE_SURFACE.privilege,
  },
  { label: 'Nachrichten', to: '/inbox', roles: ROLE_GROUPS.staff },
];

const itemClass =
  'flex items-center justify-between gap-3 rounded-md px-3 py-2 text-sm text-ink ' +
  'cursor-pointer data-[selected=true]:bg-paper';

export function CommandPalette({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const { group, propertyIds } = useObjectScope();
  const { user } = useAuth();
  const [query, setQuery] = useState('');

  const canSearch = hasAnyRole(user, ...ROLE_GROUPS.staffOwner);
  const { hits, truncated, isPending } = useQuickSearch(query, propertyIds, open && canSearch);
  const groups = groupHits(hits, canManage(user));
  const needle = query.trim().toLowerCase();
  const navMatches = NAV_ITEMS.filter(
    (item) =>
      (item.privilege ? can(user, item.privilege) : canAccess(user, item.roles)) &&
      item.label.toLowerCase().includes(needle),
  );

  const go = (nav: HitNavigation) => {
    onOpenChange(false);
    setQuery('');
    void navigate(nav);
  };

  return (
    <Command.Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) setQuery('');
      }}
      label="Suche und Aktionen"
      shouldFilter={false}
      overlayClassName="fixed inset-0 z-40 bg-navy/40 backdrop-blur-sm"
      contentClassName="fixed left-1/2 top-[14vh] z-50 w-[92vw] max-w-xl -translate-x-1/2 overflow-hidden rounded-lg border border-ink-soft/15 bg-card shadow-xl"
    >
      <DialogTitle className="sr-only">Suche und Aktionen</DialogTitle>
      <DialogDescription className="sr-only">
        Navigation und Schnellsuche in den freigegebenen StayOn-Daten.
      </DialogDescription>
      <div className="border-ink-soft/15 flex items-center gap-2 border-b px-3 py-2.5">
        <I name="Search" size={17} className="text-ink-soft" />
        <Command.Input
          value={query}
          onValueChange={setQuery}
          placeholder="Suchen — Objekt, Apartment, Buchung, Rechnung …"
          className="text-ink placeholder:text-ink-faint flex-1 bg-transparent text-sm outline-none"
        />
      </div>
      <p className="text-ink-faint px-3 pt-2 text-xs">
        Scope: {group?.name ?? 'Alle erlaubten Objekte'}
      </p>
      <Command.List className="max-h-[52vh] overflow-y-auto p-2">
        {isPending && (
          <Command.Loading className="text-ink-faint px-3 py-2 text-xs">
            Suche läuft …
          </Command.Loading>
        )}
        {navMatches.length > 0 && (
          <Command.Group
            heading="Navigation"
            className="[&_[cmdk-group-heading]]:text-ink-faint [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider"
          >
            {navMatches.map((item) => (
              <Command.Item
                key={item.to}
                value={`nav:${item.to}`}
                onSelect={() => go({ to: item.to })}
                className={itemClass}
              >
                {item.label}
              </Command.Item>
            ))}
          </Command.Group>
        )}
        {groups.map((g) => (
          <Command.Group
            key={g.kind}
            heading={g.label}
            className="[&_[cmdk-group-heading]]:text-ink-faint [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider"
          >
            {g.items.map((hit) => (
              <Command.Item
                key={`${hit.kind}:${hit.id}`}
                value={`${hit.kind}:${hit.id}`}
                onSelect={() => go(hitToNavigation(hit))}
                className={itemClass}
              >
                <span className="truncate">{hit.title}</span>
                {hit.subtitle && (
                  <span className="text-ink-soft shrink-0 truncate text-xs">{hit.subtitle}</span>
                )}
              </Command.Item>
            ))}
          </Command.Group>
        ))}
        {canSearch && (
          <Command.Item
            value="advanced-search"
            onSelect={() => go({ to: '/search/advanced' })}
            className={`${itemClass} border-ink-soft/10 text-ink-soft mt-1 border-t`}
          >
            Erweiterte Suche öffnen
          </Command.Item>
        )}
        <Command.Empty className="text-ink-soft px-3 py-6 text-center text-sm">
          Keine Treffer.
        </Command.Empty>
      </Command.List>
      {truncated && (
        <p className="border-ink-soft/10 text-ink-faint border-t px-3 py-2 text-xs">
          Weitere Treffer vorhanden — Suche verfeinern.
        </p>
      )}
    </Command.Dialog>
  );
}
